import { CdkDrag, CdkDragDrop, CdkDragEnd, CdkDragStart, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { PageScrollContentComponent } from '../../../common-shared-module/components/page-scroll-content/page-scroll-content.component';
import { ToolHelpComponent } from '../../../common-shared-module/components/tool-help/tool-help.component';
import { ItemTypeMetadata } from '../../../common-shared-module/model/dyn-form/form-metadata/item-type-metadata.enum';
import { ItemMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/item/item-metadata.model';
import { ExtendedLayoutMetadata } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/extends-layout.metadata.model';
import { SectionLayoutMetadata } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/section-layout.model';
import { CustomMyTool } from '../../models/dd-custom-tool.model';
import { DDElement } from '../../models/dd-element.model';
import { MyToolFactory } from '../../models/dd-my-tool.model';
import { Layout } from '../../models/layout.model';
import { MetadataChange } from '../../models/metadata-change.model';
import { RowLayout } from '../../models/row-layout.model';
import { SelectedLayout } from '../../models/selected-layout.model';
import { SelectedWidget } from '../../models/selected-widget.model';
import { DdSupportService } from '../../services/dd-support/dd-support.service';
import { SectionMetadataService } from '../../services/section-metadata/section-metadata.service';
import { ItemMetadataEditorComponent } from './item-metadata-editor/item-metadata-editor.component';
import { LayoutMetadataEditorComponent } from './layout-metadata-editor/layout-metadata-editor.component';
import { SectionMetadataEditorComponent } from './section-metadata-editor/section-metadata-editor.component';
import { ToolsDialogComponent } from './tools-dialog/tools-dialog.component';

type DetailType = 'none' | 'item' | 'row' | 'layout' | 'section';

enum TabType {
    Editor = 0,
    Detail = 1
}

@Component({
    selector: 'app-form-editor-section-tools',
    templateUrl: './form-editor-section-tools.component.html',
    styleUrl: './form-editor-section-tools.component.scss',
    standalone: true,
    imports: [
        ItemMetadataEditorComponent, LayoutMetadataEditorComponent, SectionMetadataEditorComponent,
        PageScrollContentComponent, ToolHelpComponent,

        CommonModule, DragDropModule,

        MatIconModule, MatTabsModule, MatAccordion, MatExpansionModule, 
    ]
})
export class FormEditorSectionToolsComponent implements OnInit {

    @Output()
    startDrag: EventEmitter<CdkDragStart> = new EventEmitter()

    @Output()
    endDrag: EventEmitter<CdkDragEnd> = new EventEmitter()

    @Output()
    changeItem: EventEmitter<MetadataChange> = new EventEmitter()

    @Output()
    addTool: EventEmitter<DDElement> = new EventEmitter()


    tools: RowLayout = new RowLayout()

    detailType: DetailType = "none"

    selectedWidget!: SelectedWidget
    item: ItemMetadataBase | null = null

    selectedLayout!: SelectedLayout
    layout!: ExtendedLayoutMetadata

    section!: SectionLayoutMetadata

    selectedTab: TabType = TabType.Editor

    layoutTools : CustomMyTool [] = [
        new CustomMyTool("Repeat", "Repetir", "repeat_one"),
        new CustomMyTool("Table", "Taula", "table_rows"),
        new CustomMyTool("WebService", "WebService", "public")
    ]

    constructor(public ddService: DdSupportService, protected sectionMetadataService: SectionMetadataService, protected dialog: MatDialog) { }

    ngOnInit(): void {
        this.tools.widgets = [
            MyToolFactory.newTool(ItemTypeMetadata.Text, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.Numeric, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.TextArea, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.CheckBox, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.MultiCheckBox, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.Datepicker, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.Time, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.Radio, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.Select, this.sectionMetadataService),
            MyToolFactory.newTool(ItemTypeMetadata.Html, this.sectionMetadataService),
        ]
    }

    neverEnterPredicate = (drag: CdkDrag, drop: CdkDropList): boolean => {
        return false
    }

    drop(event: CdkDragDrop<DDElement[]>) {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }

    startDD($event: CdkDragStart, widget: DDElement) {
        this.startDrag.emit($event)
        this.ddService.ddElement = widget
        this.ddService.clearSelection()
    }

    removeSelection($event: CdkDragStart) {
        this.startDrag.emit($event)
        this.ddService.clearSelection()
    }

    endDD($event: CdkDragEnd) {
        this.endDrag.emit($event)
        this.ddService.ddElement = null
    }

    selectSection(item: SectionLayoutMetadata) {
        this.selectedTab = TabType.Detail
        this.detailType = "section"
        this.section = item
    }
    
    selectLayout(item: SelectedLayout) {
        this.selectedTab = TabType.Detail
        this.detailType = "layout"
        this.selectedLayout = item
        this.layout = item.row.layout()
    }

    selectWidget(item: SelectedWidget) {
        this.selectedTab = TabType.Detail
        this.detailType = "item"
        this.item = item.widget.item()
        this.selectedWidget = item
    }

    onGoParentWidget() {
        if (this.selectedWidget.row.isInLayout()) {
            this.selectLayout({ row: <Layout> this.selectedWidget.row.layout, index: 0 })
            return
        } else {
            this.selectSection(this.sectionMetadataService.sectionMetadata)
        }

    }

    onGoParentLayout() {
        this.selectSection(this.sectionMetadataService.sectionMetadata)
    }

    selectToolTab() {
        this.selectedTab = TabType.Editor
    }

    onFireChange(metadataChange: MetadataChange) {
        this.changeItem.emit(metadataChange)
    }

    addWidget(widget: DDElement) {
        this.addTool.emit(widget.clone())
    }

    showToolsToAdd(): MatDialogRef<ToolsDialogComponent, DDElement> {
        // Remove focus before open dialog for Accessibility //
        (document.activeElement as HTMLElement)?.blur()
        return this.dialog.open(ToolsDialogComponent, { data: { tools: this.tools, layoutTools: this.layoutTools } })
    }

    changeTab(tabIndex: number) {
        this.selectedTab = (tabIndex == 1) ? TabType.Detail : TabType.Editor
    }
    
}
